
import { Button } from "@/components/ui/button";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { toast } from "sonner";

interface ReviewVoteButtonsProps {
  reviewId: string;
  initialUpvotes: number;
  initialDownvotes: number;
  disabled?: boolean;
}

export const ReviewVoteButtons = ({ 
  reviewId, 
  initialUpvotes = 0, 
  initialDownvotes = 0,
  disabled = false
}: ReviewVoteButtonsProps) => {
  const [userVote, setUserVote] = useState<'up' | 'down' | null>(null);
  const [upvotes, setUpvotes] = useState(initialUpvotes);
  const [downvotes, setDownvotes] = useState(initialDownvotes);
  const [isVoting, setIsVoting] = useState(false);
  
  // Calculate the net vote count (upvotes minus downvotes)
  const voteCount = upvotes - downvotes;

  // Fetch actual vote counts and user's current vote
  useEffect(() => {
    if (disabled || !reviewId) return;

    const fetchVoteData = async () => {
      try {
        // Get user's vote if they're logged in
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: userVoteData } = await supabase
            .from('review_votes')
            .select('vote_type')
            .eq('review_id', reviewId)
            .eq('user_id', user.id)
            .maybeSingle();

          if (userVoteData) {
            setUserVote(userVoteData.vote_type === 'upvote' ? 'up' : 'down');
          }
        }

        // Get exact count of upvotes
        const { count: upvoteCount, error: upvoteError } = await supabase
          .from('review_votes')
          .select('*', { count: 'exact', head: true })
          .eq('review_id', reviewId)
          .eq('vote_type', 'upvote');
          
        if (upvoteError) throw upvoteError;
        
        // Get exact count of downvotes
        const { count: downvoteCount, error: downvoteError } = await supabase
          .from('review_votes')
          .select('*', { count: 'exact', head: true })
          .eq('review_id', reviewId)
          .eq('vote_type', 'downvote');
          
        if (downvoteError) throw downvoteError;

        // Update state with actual counts
        setUpvotes(upvoteCount || 0);
        setDownvotes(downvoteCount || 0);
      } catch (error) {
        console.error('Error fetching vote data:', error);
      }
    };

    fetchVoteData();
  }, [reviewId, disabled]);

  const handleVote = async (voteType: 'up' | 'down') => {
    // Don't do anything if buttons are disabled
    if (disabled) return;
    
    // Prevent event propagation to stop the click from bubbling up to parent link elements
    event?.stopPropagation();
    event?.preventDefault();
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Please sign in to vote on reviews');
      return;
    }
    
    if (isVoting) return;
    setIsVoting(true);
    
    try {
      // Convert 'up'/'down' to 'upvote'/'downvote' for database consistency
      const dbVoteType = voteType === 'up' ? 'upvote' : 'downvote';
      
      // If clicking the same vote type that's already active, remove the vote
      if (userVote === voteType) {
        const { error } = await supabase
          .from('review_votes')
          .delete()
          .eq('review_id', reviewId)
          .eq('user_id', user.id);

        if (error) throw error;

        // Update local state
        if (voteType === 'up') {
          setUpvotes(prev => Math.max(0, prev - 1));
        } else {
          setDownvotes(prev => Math.max(0, prev - 1));
        }
        
        setUserVote(null);
        toast.success('Vote removed');
      } 
      // If switching vote type or voting for the first time
      else {
        // If user already voted but is changing their vote
        if (userVote) {
          // First update the vote in the database
          const { error } = await supabase
            .from('review_votes')
            .update({ vote_type: dbVoteType })
            .eq('review_id', reviewId)
            .eq('user_id', user.id);

          if (error) throw error;

          // Then update local state
          if (userVote === 'up') {
            setUpvotes(prev => Math.max(0, prev - 1));
            setDownvotes(prev => prev + 1);
          } else {
            setDownvotes(prev => Math.max(0, prev - 1));
            setUpvotes(prev => prev + 1);
          }
        } 
        // First time voting
        else {
          const { error } = await supabase
            .from('review_votes')
            .insert({
              review_id: reviewId,
              user_id: user.id,
              vote_type: dbVoteType
            });

          if (error) throw error;

          // Update local state
          if (voteType === 'up') {
            setUpvotes(prev => prev + 1);
          } else {
            setDownvotes(prev => prev + 1);
          }
        }
        
        setUserVote(voteType);
        toast.success(`Review ${voteType === 'up' ? 'upvoted' : 'downvoted'}`);
      }
    } catch (error) {
      console.error('Error updating vote:', error);
      toast.error('Failed to update vote');
    } finally {
      setIsVoting(false);
    }
  };

  return (
    <div className="flex items-center gap-1 bg-secondary/30 rounded-md px-2 py-1">
      <Button 
        variant="ghost" 
        size="sm" 
        className={`h-8 flex-shrink-0 ${userVote === 'up' ? 'text-green-500' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleVote('up');
        }}
        disabled={isVoting || disabled}
        title="Upvote"
      >
        <ThumbsUp className={`h-4 w-4 ${userVote === 'up' ? 'fill-current' : ''}`} />
      </Button>
      
      <span className={`text-sm font-medium min-w-6 text-center ${voteCount > 0 ? 'text-green-500' : voteCount < 0 ? 'text-red-500' : ''}`}>
        {voteCount}
      </span>
      
      <Button 
        variant="ghost" 
        size="sm" 
        className={`h-8 flex-shrink-0 ${userVote === 'down' ? 'text-red-500' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleVote('down');
        }}
        disabled={isVoting || disabled}
        title="Downvote"
      >
        <ThumbsDown className={`h-4 w-4 ${userVote === 'down' ? 'fill-current' : ''}`} />
      </Button>
    </div>
  );
};
